/**
 * Pure logic for the Poll / Quick Quiz precedence editor.
 *
 * Kept out of the component and unit-tested, following the FanWave screen — the only CMS screen with
 * colocated tests for its pure logic. Worth the discipline here because a malformed order is not a
 * cosmetic bug: it decides what every fan sees on every team channel, and the failure mode is a
 * blank surface rather than an error message.
 *
 * ONE ORDER. This module used to model a ten-row table carrying an ordering per game phase and per
 * surface, which the editor then flattened for display and fanned back out on save. The backend has
 * dropped both axes, so all of that — the phase vocabulary, the normalise/fan-out pair, the
 * divergence report, the round-trip guarantee — is gone with it. What is left is the list itself.
 */

export const CLAIMS = ['quiz_answerable', 'quiz_displayable', 'poll_open', 'poll_closed'];

export const CLAIM_LABELS = {
  quiz_answerable: 'Quick Quiz — answerable',
  quiz_displayable: 'Quick Quiz — results',
  poll_open: 'Poll — open for votes',
  poll_closed: 'Poll — results'
};

/**
 * One line per option, saying WHEN it is available — which is the half of the rule this screen could
 * not previously show.
 *
 * The order an admin sets is only half the decision: the server hands the slot to the first option
 * that is available at that instant, and availability is not editable here. So a ranked list of four
 * labels leaves the most common question unanswered — "why is my quiz not showing when I put it
 * first?" — and the answer is almost always that the claim above it was satisfied, or that this one
 * was not. Each subtitle names the condition rather than restating the label.
 *
 * Kept here beside CLAIM_LABELS, not in the component: this is the vocabulary of the rule, and it is
 * the backend's SurfaceClaim docblock (constants/pollQuizConfig.ts) that these have to stay true to.
 */
export const CLAIM_SUBTITLES = {
  quiz_answerable: 'While fans can still answer',
  quiz_displayable: 'After answering ends, showing the quiz answers.',
  poll_open: 'While the poll is accepting votes.',
  poll_closed: 'After voting ends, showing the result percentages.'
};

/**
 * The default order — mirrors DEFAULT_CLAIM_ORDER in the backend's constants/pollQuizConfig.ts.
 * Keep the two in step: this drives "Reset to defaults", so a drift would have the CMS reset admins
 * to an order the server never shipped.
 *
 * `poll_open` leads, on the client's literal reading of their Scenario 5 — a newly opened poll takes
 * precedence immediately, even over a quiz fans can still play. Then the playable quiz, then live
 * poll results, then finished quiz results: each module's open state before either module's results.
 *
 * `poll_closed` above `quiz_displayable` is the client's call, reversed from what shipped first, and
 * it has a consequence worth knowing before an admin is told the tree is "just preferences": a closed
 * poll holds the slot for as long as its match is current, so a quiz gives the surface back the
 * moment it stops accepting answers. Its visibility window and custom end time are then never
 * reached on a game day that also has a poll — only during the half-time break, when the game-day
 * poll reports neither open nor closed, and off game day when no poll is active.
 */
export const DEFAULT_ORDER = ['poll_open', 'quiz_answerable', 'poll_closed', 'quiz_displayable'];

/**
 * Reads the stored order into the one the editor shows — always COMPLETE, always in a usable order.
 *
 * Three jobs, each covering a way the stored value can fail to be editable.
 *
 * Falls back to the default for anything unusable — absent, empty, or not an array. Not defensiveness
 * for its own sake: a document written before the backend collapsed its per-phase table comes back
 * with no `claimOrder` at all, and the server itself falls back the same way, so showing the default
 * is showing what is genuinely in force.
 *
 * Drops unknown and repeated claims. The editor has no node that can render an unknown one, and a
 * repeat is unreachable on the server anyway, so keeping either would mean Save posting back
 * something the endpoint rejects.
 *
 * APPENDS any missing claim, lowest priority. This is the one that changes what an admin sees: the
 * editor can no longer add or remove options, only reorder them, so a short stored list would be a
 * dead end — four rows of tree with no way to reach the missing option ever again. Appending puts it
 * last, which is the least disruptive place: a claim only matters once the ones above it are all
 * unsatisfied, so nothing visibly changes until then. Worth being explicit that this DOES alter the
 * stored config if the admin then saves — a list that deliberately omitted a claim comes back with it
 * present but ranked last. Nothing is written until Save.
 */
export const readOrder = (claimOrder) => {
  const stored = Array.isArray(claimOrder) ? claimOrder : [];
  const known = stored.filter((claim) => CLAIMS.includes(claim));
  const deduped = known.filter((claim, idx) => known.indexOf(claim) === idx);
  if (!deduped.length) return [...DEFAULT_ORDER];

  // Missing claims in DEFAULT_ORDER's sequence rather than CLAIMS', so a completed list reads in the
  // shipped order at the tail instead of in whatever order the constant happens to declare.
  const missing = DEFAULT_ORDER.filter((claim) => !deduped.includes(claim));
  return [...deduped, ...missing];
};

/**
 * Client-side check mirroring the endpoint, so an admin sees the problem before submitting.
 *
 * Returns a list of human-readable problems; empty means the order is valid. The server validates
 * independently and remains the authority — this exists to save a round trip, not to replace it.
 *
 * Every problem it can report is structurally unreachable from this UI, which builds the order from
 * a fixed set of options: a repeat, an unknown option and an empty list all take a bug here. It runs
 * as a pre-save assertion against the built payload rather than as the admin's error display.
 */
export const validateOrder = (order) => {
  const problems = [];
  const claims = order ?? [];

  const dupes = claims.filter((c, i) => claims.indexOf(c) !== i);
  if (dupes.length) {
    problems.push(
      `${[...new Set(dupes)].map((d) => CLAIM_LABELS[d] ?? d).join(', ')} listed twice. ` +
        'A repeated option can never be reached.'
    );
  }

  const unknown = claims.filter((c) => !CLAIMS.includes(c));
  if (unknown.length) {
    problems.push(`Unknown option ${[...new Set(unknown)].join(', ')}.`);
  }

  if (!claims.length) {
    // The endpoint rejects this outright. An empty order used to be legal per phase, meaning "show
    // nothing here"; as the single global order it would turn the surface off for every team at
    // once, and it cannot be told apart from a document predating the field.
    problems.push(
      'At least one option must be listed. To show nothing, deactivate the polls and quizzes ' +
        'themselves rather than emptying this list.'
    );
  }

  return problems;
};

/** The payload shape the update endpoint expects. */
export const orderPayload = (order) => ({ claimOrder: [...(order ?? [])] });

/**
 * Moves a claim within one ordered list. Returns the input unchanged when the move is impossible.
 *
 * The only edit the editor can make. `toggleClaimInList`, which added and removed claims, went with
 * the add/remove controls — with the option set fixed, there is nothing for it to do.
 */
export const moveClaimInList = (claims, claim, direction) => {
  const current = claims ?? [];
  const next = [...current];
  const from = next.indexOf(claim);
  const to = from + (direction === 'up' ? -1 : 1);
  if (from === -1 || to < 0 || to >= next.length) return claims;
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
};
